import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  hasUnfinishedGoals,
  getProjectPhasesWithSprints,
  getAllSprintsByPhase,
  getGoalDetails,
  getGoalsByPhase,
  getTaskActivityLog,
  getReleaseVersions,
} from "../api/scheduleAPI";
import { client } from "../../utils/axiosClient";
import { useAppDispatch } from "../hooks";
import { updateSchedule } from "../slices/schedule.slice";

const queryConfig = {
  enabled: true,
  refetchInterval: 1800000,
  staleTime: Infinity,
  placeholderData: (prev: any) => prev,
};

export const useHasUnfinishedGoals = ({
  phaseId, projectId,
}: {
  phaseId: string; projectId: string;
}) => {
  return useQuery({
    queryKey: ["unfinishedGoals", phaseId, projectId],
    queryFn: () => hasUnfinishedGoals({ phaseId, projectId }),
  });
};

export const useGetProjectPhases = (projectId) => {
  return useQuery({
    queryKey: ["projectPhases", projectId],
    queryFn: async () => {
      if (!projectId) throw new Error("Project ID is required to fetch project phases.");
      const { data } = await client.get(`/project/project-phases?projectId=${projectId}`);
      return data;
    },
    enabled: !!projectId,
    refetchInterval: 1800000,
    staleTime: Infinity,
    placeholderData: (prev: any) => prev,
  });
};

export const useGetProjectPhasesWithSprints = ({ projectId }: { projectId: string }) => {
  return useQuery({
    queryKey: ["projectPhasesWithSprints", projectId],
    queryFn: () => getProjectPhasesWithSprints({ projectId }),
  });
};

export const useGetSprintsByPhase = ({
  projectId, phaseId,
}: {
  projectId: string; phaseId?: string;
}) => {
  return useQuery({
    queryKey: ["sprintsByPhase", projectId, phaseId],
    queryFn: async () => {
      const response = await client.get(`/schedule/sprints`, { params: { projectId, phaseId } });
      return response.data;
    },
    ...queryConfig,
  });
};

export const useGetAllSprintsByPhase = ({
  projectId, phaseId,
}: {
  projectId: string; phaseId: number;
}) => {
  return useQuery({
    queryKey: ["allSprintsByPhase", projectId, phaseId],
    queryFn: () => getAllSprintsByPhase({ projectId, phaseId }),
  });
};

export const useGetGoalsByLabelId = ({
  labelId, projectId, filters, enabled = true,
}) => {
  return useQuery({
    queryKey: ["goalsByLabel", labelId, projectId, filters],
    queryFn: async () => {
      const { data } = await client.get(`schedule/goals-by-label`, {
        params: { labelId, projectId, ...filters },
      });
      return data;
    },
    enabled: !!labelId && enabled,
    refetchInterval: 1800000,
    staleTime: Infinity,
    placeholderData: (prev: any) => prev,
  });
};

export const useGetGoalsBySprint = ({
  projectId, sprintId, filters, enabled = true, limit, offset,
}: {
  projectId: any; sprintId: any; filters?: any;
  enabled?: boolean; limit?: number; offset?: number;
}) => {
  return useQuery({
    queryKey: ["goalsBySprint", projectId, sprintId, offset],
    queryFn: async () => {
      const response = await client.get(`schedule/goals`, {
        params: { sprintId, projectId, ...filters, limit, offset },
      });
      return response.data;
    },
    enabled,
    refetchInterval: 1800000,
    staleTime: Infinity,
    placeholderData: (prev: any) => prev,
  });
};

export const useGetActiveSprint = ({ projectId }: { projectId: string }) => {
  return useQuery({
    queryKey: ["activeSprint", projectId],
    queryFn: async () => {
      try {
        const { data } = await client.get("/schedule/activeSprint", { params: { projectId } });
        return data.activeSprint;
      } catch (error) {
        throw new Error("Failed to fetch active sprint");
      }
    },
    ...queryConfig,
  });
};

export const useGetTodoList = ({
  projectId, limit, offset,
}: {
  projectId: string; limit?: number; offset?: number;
}) => {
  const dispatch = useAppDispatch();
  return useQuery({
    queryKey: ["todoList", projectId],
    queryFn: async () => {
      const response = await client.get(`schedule/goals`, {
        params: { projectId, limit, offset, sprintId: "null" },
      });
      await dispatch(updateSchedule({ key: "todoListCount", value: response.data.totalRecords }));
      return response.data;
    },
    ...queryConfig,
  });
};

export const useUpdateGoal = (): UseMutationResult<any, unknown, object, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: object) => {
      try {
        const response = await client.put("schedule/update-goal", payload);
        return response.data;
      } catch (error) {
        throw new Error("Failed to update goal");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todoList"] });
    },
  });
};

export const useGetGoalDetails = ({ goalId }: { goalId: string }) => {
  return useQuery({
    queryKey: ["goalDetails", goalId],
    queryFn: () => getGoalDetails({ goalId }),
  });
};

export const useGetGoalsByPhase = ({
  projectId, phaseId,
}: {
  projectId: string; phaseId: string;
}) => {
  return useQuery({
    queryKey: ["goalsByPhase", projectId, phaseId],
    queryFn: () => getGoalsByPhase({ projectId, phaseId }),
  });
};

export const useGetSwimlanes = ({ projectId }: { projectId: string }) => {
  return useQuery({
    queryKey: ["swimlanes", projectId],
    queryFn: async () => {
      const response = await client.get(`schedule/swimlane?projectId=${projectId}`);
      return response.data;
    },
    refetchInterval: 1800000,
    staleTime: 86400000,
    placeholderData: (prev: any) => prev,
  });
};

export const useGetLabels = (projectId) => {
  return useQuery({
    queryKey: ["labels", projectId],
    queryFn: async () => {
      const response = await client.get(`schedule/label?projectId=${projectId}`);
      return response.data;
    },
    refetchInterval: 1800000,
    staleTime: 86400000,
    placeholderData: (prev: any) => prev,
    enabled: !!projectId,
  });
};

export const useGetTaskActivityLog = ({ goalId }: { goalId: string }) => {
  return useQuery({
    queryKey: ["taskActivityLog", goalId],
    queryFn: () => getTaskActivityLog({ goalId }),
  });
};

export const useGetReleaseVersions = ({ projectId }: { projectId: string }) => {
  return useQuery({
    queryKey: ["releaseVersions", projectId],
    queryFn: () => getReleaseVersions({ projectId }),
  });
};